import { createFileRoute, Outlet } from "@tanstack/react-router";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { NOTIFICATION_LIST_LIMIT } from "~/components/common/notifications/notification-panel";
import type { NotificationsQueryVariables } from "~/graphql/graphql.gen";
import {
  useCurrentUserQuery,
  useInfiniteNotificationsQuery,
  useNotificationsUnreadCountQuery,
} from "~/graphql/graphql.gen";
import { useNotificationCreated } from "~/hooks/use-notification-created";
import { useSnippetCreated } from "~/hooks/use-snippet-created";
import { useSnippetTagCreated } from "~/hooks/use-snippet-tag-created";
import { useSnippetTagUpdated } from "~/hooks/use-snippet-tag-updated";
import { useSnippetUpdated } from "~/hooks/use-snippet-updated";
import { useSyncSnippetTags } from "~/hooks/use-sync-snippet-tags";
import { useSyncSnippets } from "~/hooks/use-sync-snippets";
import { useSyncTickets } from "~/hooks/use-sync-tickets";
import { useTicketCreated } from "~/hooks/use-ticket-created";
import { useTicketDeleted } from "~/hooks/use-ticket-deleted";
import { useTicketUpdated } from "~/hooks/use-ticket-updated";
import {
  NOTIFICATION_TAB_UNREAD,
  useNotificationTabStore,
} from "~/stores/notification-tab-store";

export const Route = createFileRoute("/_auth/_app_shell")({
  component: RouteComponent,
});

function RouteComponent() {
  useNotificationCreated();
  const { tab } = useNotificationTabStore();
  useNotificationsUnreadCountQuery(undefined);
  const query: NotificationsQueryVariables = {
    filters: tab === NOTIFICATION_TAB_UNREAD ? { read: false } : undefined,
    first: NOTIFICATION_LIST_LIMIT,
  };
  useInfiniteNotificationsQuery(
    {
      ...query,
    },
    {
      initialPageParam: undefined,
      getNextPageParam: (lastPage) => {
        return lastPage.supportNotifications.pageInfo.hasNextPage
          ? {
              ...query,
              after: lastPage.supportNotifications.pageInfo.endCursor,
            }
          : undefined;
      },
    },
  );

  useSyncSnippets();
  useSnippetCreated();
  useSnippetUpdated();

  useSyncSnippetTags();
  useSnippetTagCreated();
  useSnippetTagUpdated();

  useSyncTickets();
  useTicketCreated();
  useTicketUpdated();
  useTicketDeleted();

  const { data: user } = useCurrentUserQuery(undefined, {
    select: (data) => data.currentUser,
  });
  const { i18n } = useTranslation();

  useEffect(() => {
    if (!user) return;

    const userLanguage =
      user.__typename === "ActorInternalUser"
        ? user.internalUser.language
        : user.supplierUser.language;
    if (i18n.language !== (userLanguage as string)) {
      void i18n.changeLanguage(userLanguage);
    }
  }, [i18n, user]);

  return <Outlet />;
}
